// Signing in and out through the API-held session (E12-S1-T2). The web app's part is small and
// stays small:
//
//   - Who is signed in is the API's answer to GET /auth/me, cached by TanStack Query
//     (useSession, from src/auth/session-state.tsx).
//   - Signing in is a top-level navigation to GET /auth/login?returnTo=<a path on this origin>
//     (src/auth/sign-in.tsx). The API sends the browser to the identity provider, its callback sets
//     the httpOnly session cookie, and the browser comes back to the return path.
//   - Signing out is one POST /auth/logout with the CSRF header. The API answers with the address to
//     go to — the identity provider's end-session URL, or a local page — the query cache is emptied,
//     and the browser goes there by a top-level navigation, so the provider's session ends too.
//   - A session that ends elsewhere (expiry, back-channel logout) is a 401 on the next request,
//     which the API client turns into the signed-out state (src/app.tsx).
//
// Nothing here reads a token, a URL fragment or web storage, or sets an Authorization header: the
// cookie is the API's, and the browser sends it.
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { z } from "zod";
import { isApiError } from "../api/errors.js";
import { useApi } from "../api/provider.js";
import { endSessionAddress } from "./return-path.js";
import {
  currentPath,
  useSessionState,
  useSessionStore,
  type SessionState,
} from "./session-state.js";

/**
 * POST /auth/logout's answer (E15-S4): where the browser goes next — an absolute https end-session
 * URL, or a path on this origin starting with a slash. Strict, so a change in the shape shows.
 */
export const LogoutAnswer = z.strictObject({ redirectTo: z.string().min(1) });

/** Who is signed in, as the API last answered: checking, signed-in, signed-out or failed. */
export function useSession(): SessionState {
  return useSessionState();
}

/**
 * Ends the session: one POST /auth/logout (the client adds X-CSRF-Token), then the query cache is
 * emptied and the browser leaves for the address the API answered. A 401 means the session had
 * already ended: the app shows signed-out and nothing is reported. Any other failure rejects, and the
 * session is still the API's.
 */
export function useSignOut(): () => Promise<void> {
  const api = useApi();
  const store = useSessionStore();
  const queryClient = useQueryClient();
  return useCallback(async () => {
    let answer: z.output<typeof LogoutAnswer>;
    try {
      answer = await api.post("/auth/logout", LogoutAnswer);
    } catch (err) {
      if (isApiError(err) && err.kind === "unauthorised") {
        // The client's onUnauthorised has already flipped the app to signed-out.
        queryClient.clear();
        return;
      }
      throw err;
    }
    // Signed-out first, so nothing asks the API again while the browser leaves.
    store.signOut(currentPath());
    queryClient.clear();
    store.leave(endSessionAddress(answer.redirectTo));
  }, [api, store, queryClient]);
}
